// components/GeneratedThumb/GeneratedThumb.tsx — deterministic CSS/SVG-generated
// thumbnail art. No binary assets, ever — same precedent as PostCard's Cityscape/
// Globe decorative media (src/components/PostCard/media/decorative/).
//
// Shared substrate: both the Videos page and the Live page render their
// card/hero/rail art through this one component, keyed by a stable seed string
// (a video id, a creator handle, whatever uniquely identifies the item) so the
// same item always renders the same art — verified by hashSeed.test.ts and
// GeneratedThumb.test.tsx.
//
// `live` (slice-4-xl PR 2) adds a subtle red-tinted overlay so Live channel art
// reads distinctly from Videos art at a glance, without touching the
// deterministic hash/hue/bar math above it — same seed still always produces
// the same base art, live or not.
//
// Layering note (slice-4-xl S4 fix round): the hue-rotate filter must apply
// ONLY to the pattern/Glow/Bars layer, never to the whole component root — the
// live veil used to sit inside the hue-rotated element, so it rendered
// green/blue/purple for most seeds instead of red. `RotatedLayer` carries the
// filter; `LiveVeil` is a sibling of it (inside the unrotated `Wrapper`), so
// it stays red regardless of the seed's hue.

import styled, { keyframes } from 'styled-components';
import { hashSeed, barsFromSeed, hueFromSeed } from './hashSeed';

// Kept aligned with mobile's GeneratedThumb (mobile/src/components/GeneratedThumb.tsx).
const BAR_COUNT = 9;

const scanline = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 0 48px; }
`;

const Wrapper = styled.div<{ $fill?: boolean }>`
  position: relative;
  width: 100%;
  height: ${({ $fill }) => ($fill ? '100%' : '160px')};
  overflow: hidden;
  border-radius: inherit;
  background: ${({ theme }) => theme.color.bgRoot};
`;

const RotatedLayer = styled.div<{ $hue: number }>`
  position: absolute;
  inset: 0;
  filter: hue-rotate(${({ $hue }) => $hue}deg);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        ${({ theme }) => theme.color.brandFrom}22 1px,
        transparent 1px
      ),
      linear-gradient(90deg, ${({ theme }) => theme.color.brandFrom}22 1px, transparent 1px);
    background-size: 32px 32px;
    animation: ${scanline} 4s linear infinite;
    opacity: 0.45;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
    }
  }
`;

const Bars = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-evenly;
  height: 70%;
  padding: 0 6px;
`;

const Bar = styled.div<{ $h: number }>`
  width: clamp(6px, 6%, 14px);
  height: ${({ $h }) => $h}%;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.color.brandFrom} 0%,
    ${({ theme }) => theme.color.brandTo} 100%
  );
  border-radius: 2px 2px 0 0;
  opacity: 0.85;
  box-shadow: 0 0 6px ${({ theme }) => theme.color.brandFrom}88;
`;

const Glow = styled.div`
  position: absolute;
  top: 18%;
  left: 50%;
  transform: translateX(-50%);
  width: 46%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    ${({ theme }) => theme.color.accentCyan}55 0%,
    transparent 70%
  );
`;

// Deliberately OUTSIDE RotatedLayer (see the file-level layering note) so it
// always reads as red, independent of the seed's hue.
const LiveVeil = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.color.danger}22;
  pointer-events: none;
`;

export interface GeneratedThumbProps {
  /** Stable identity — same key always produces the same art. Required. */
  seedKey: string;
  /** Fill the parent (100% height) instead of the default fixed 160px. */
  fill?: boolean;
  /** Subtle red-tinted overlay for Live channel art (slice-4-xl PR 2). Does not
   *  change the deterministic hash/hue/bar math — same seed, same base art. */
  live?: boolean;
}

/** Deterministic CSS-generated thumbnail art, purely decorative. */
export function GeneratedThumb({ seedKey, fill = false, live = false }: GeneratedThumbProps) {
  const seed = hashSeed(seedKey);
  const bars = barsFromSeed(seed, BAR_COUNT);
  const hue = hueFromSeed(seed);

  return (
    <Wrapper $fill={fill} aria-hidden="true" data-testid="generated-thumb">
      <RotatedLayer $hue={hue} data-testid="generated-thumb-rotated">
        <Glow />
        <Bars>
          {bars.map((h, i) => (
            <Bar key={i} $h={h} />
          ))}
        </Bars>
      </RotatedLayer>
      {live && <LiveVeil data-testid="generated-thumb-live-veil" />}
    </Wrapper>
  );
}
